import { useState, useEffect } from "react";
import Loading from "../../animation/loading";

/**
 * Fee rows — bookings-hub style: white surface, hairline borders, outlined save.
 */
const CardList = ({ cards, handleSetFee, loading }) => {
  const [cardAmounts, setCardAmounts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    const initialAmounts = (cards || []).reduce((acc, card) => {
      acc[card.feename || card.title] =
        card.amount === 0 || card.amount ? card.amount : "";
      return acc;
    }, {});
    setCardAmounts(initialAmounts);
  }, [cards]);

  if (loading && !(cards || []).length) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loading />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#E3E8F0] bg-white overflow-hidden">
      <div className="hidden sm:grid grid-cols-[1fr_160px_120px] gap-3 px-4 py-2.5 border-b border-[#EEF1F6] bg-[#F8FAFC]">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#6D7C94]">
          Fee
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#6D7C94]">
          Amount
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#6D7C94] text-right">
          Action
        </span>
      </div>
      <div className="divide-y divide-[#EEF1F6]">
        {(cards || []).map((card) => {
          const key = card.feename || card.title;
          const value = cardAmounts[key];
          const dirty =
            String(value ?? "") !== String(card.amount === 0 || card.amount ? card.amount : "");
          return (
            <div
              key={key}
              className="grid grid-cols-1 sm:grid-cols-[1fr_160px_120px] gap-2 sm:gap-3 items-center px-4 py-3.5 hover:bg-[#F8FAFC]/50"
            >
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-[#0F172A]">
                  {card.title}
                </p>
                {card.hint ? (
                  <p className="text-[11px] text-[#6D7C94] mt-0.5">{card.hint}</p>
                ) : null}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] font-semibold text-[#6D7C94]">
                  ₦
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={value ?? ""}
                  onChange={(event) => {
                    const next = event.target.value.replace(/[^\d.]/g, "");
                    setCardAmounts((prev) => ({ ...prev, [key]: next }));
                  }}
                  className="w-full rounded-lg border border-[#D6DEEA] bg-white pl-7 pr-3 py-2 text-[13px] font-semibold tabular-nums text-[#0F172A] outline-none focus:border-primary1 focus:ring-2 focus:ring-primary1/15"
                />
              </div>
              <div className="flex justify-end">
                {card?.btnRender ? (
                  card.btnRender(value)
                ) : (
                  <button
                    type="button"
                    disabled={loading || savingKey === key}
                    onClick={async () => {
                      setSavingKey(key);
                      try {
                        await handleSetFee(card.feename, value);
                      } finally {
                        setSavingKey(null);
                      }
                    }}
                    className={`min-h-[36px] min-w-[88px] rounded-lg border-[1.5px] px-3 text-[12.5px] font-bold transition-colors disabled:opacity-50 ${
                      dirty || card.highlight
                        ? "border-primary1 bg-primary1 text-white hover:bg-primary1/90"
                        : "border-[#D6DEEA] bg-white text-[#0F172A] hover:bg-[#F8FAFC]"
                    }`}
                  >
                    {savingKey === key ? "Saving…" : card.btn || "Save"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CardList;
